import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, MessageCircle, Sparkles } from 'lucide-react';
import ParticlesBg from '@/components/common/ParticlesBg';
import { BRAND, HERO_STATS } from '@/data/mockData';
import { HOME } from '@/constants/testIds';

const Hero = () => {
  return (
    <section className="relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 radial-glow" aria-hidden />
      <div className="absolute inset-0 grid-bg opacity-30" aria-hidden />
      <ParticlesBg count={50} />

      {/* Orbital ring (decorative) */}
      <div className="absolute -right-32 -top-32 w-[600px] h-[600px] rounded-full border border-brand-cyan/10 spin-slow hidden lg:block" aria-hidden>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-brand-cyan shadow-[0_0_25px_rgba(0,240,255,0.8)]" />
      </div>
      <div className="absolute -right-16 -top-16 w-[400px] h-[400px] rounded-full border border-brand-cyan/15 spin-slow hidden lg:block" style={{ animationDirection: 'reverse', animationDuration: '20s' }} aria-hidden>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-brand-cyanSoft shadow-[0_0_20px_rgba(0,240,255,0.7)]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 lg:pt-32 pb-20 lg:pb-28">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-4xl"
        >
          <div className="inline-flex items-center gap-2 glass rounded-full px- py-1.5 text-xs font-mono tracking-widest uppercase text-brand-cyan">
            <Sparkles className="w-3.5 h-3.5" />
            GenAI Automation Mentorship · 2026 Cohort
          </div>

          <h1
            data-testid={HOME.heroHeadline}
            className="font-display mt-6 text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.95] tracking-tight font-semibold"
          >
            Manual Testing is <span className="text-red-400/90">dying</span>
            <br />
            <span className="bg-gradient-to-r from-brand-cyan via-cyan-300 to-brand-cyan bg-clip-text text-transparent">
              GenAI is replacing QAs
            </span>
            <br />
            upgrade to GenAI Automation in 90 days
          </h1>

          <p className="mt-6 text-base sm:text-lg text-white/75 max-w-2xl leading-relaxed">
            Master <span className="text-white font-semibold">Selenium · Playwright · Appium · Cucumber BDD</span> and
            <span className="text-brand-cyan font-semibold"> GenAI Automation</span> with 18+ years of real-world industry mentorship.
            Job-oriented, project-based, conversion-driven.
          </p>

          <div className="mt-9 flex flex-wrap gap-3">
            <Link
              to="/contact"
              data-testid={HOME.heroCtaConsult}
              className="inline-flex items-center gap-2 bg-brand-cyan text-black font-semibold px-6 py-3.5 rounded-lg btn-glow hover:bg-brand-cyanSoft transition group"
            >
              Book Career Consultation
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <Link
              to="/programs"
              data-testid={HOME.heroCtaExplore}
              className="inline-flex items-center gap-2 glass border-brand-cyan/30 px-6 py-3.5 rounded-lg font-semibold text-white hover:bg-brand-cyan/10 transition"
            >
              Explore Programs
            </Link>
            <a
              href={BRAND.whatsapp}
              target="_blank"
              rel="noreferrer"
              data-testid={HOME.heroCtaWhatsapp}
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg font-semibold text-[#25D366] border border-[#25D366]/30 hover:bg-[#25D366]/10 transition"
            >
              <MessageCircle className="w-4 h-4" />
              Join WhatsApp Community
            </a>
          </div>
        </motion.div>

        {/* Stats */}
        <div
          data-testid={HOME.statsContainer}
          className="relative mt-16 lg:mt-24 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 rounded-2xl overflow-hidden border border-white/10"
        >
          {HERO_STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 + i * 0.08, duration: 0.5 }}
              className="bg-[#0B1221] px-6 py-7 lg:px-8 lg:py-9 hover:bg-[#0a1936] transition"
            >
              <div className="font-display text-3xl lg:text-5xl font-black">
                <span className="text-brand-cyan">{s.value}</span>
                <span className="text-white">{s.suffix}</span>
              </div>
              <div className="mt-2 text-[11px] tracking-[0.2em] uppercase text-brand-ink font-mono">
                {s.label}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
